import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import HeroFields from "@/components/HeroFields";

const profile: Array<[string, string]> = [
  ["Client", "Consumer-hardware manufacturer (identity withheld)"],
  ["Company scale", "$90–130M revenue"],
  ["Industry", "Consumer hardware · direct and retail channels"],
  ["Engagement", "9 months, senior team of four"],
  ["Measurement", "Weekly KPIs vs. a 12-week pre-engagement baseline"],
];

const problems: Array<[string, string]> = [
  ["Planning lived in spreadsheets", "Demand, build and purchasing plans were rebuilt by hand every week. Nobody could say which version was current."],
  ["Three systems, no shared truth", "Orders, inventory and fulfilment each held a different stock position, reconciled overnight at best."],
  ["Every launch broke something", "A new SKU meant manual setup in each system, so launch lead time grew with the catalogue."],
  ["Exceptions had no owner", "Stuck orders surfaced when a customer complained. Exception volume rose in step with order volume."],
];

const solution: Array<[string, string, string]> = [
  ["01", "Diagnose", "A two-week operating diagnostic across demand, planning, procurement, assembly and fulfilment. We named the eleven largest constraints, put a business cost on each, and sequenced them into a 90-day plan."],
  ["02", "Build", "One planning-to-fulfilment integration layer built alongside the client team, with a single stock and order position. One of the three legacy systems was retired. SKU setup became one step instead of three."],
  ["03", "Operationalise", "Exception handling given a named owner and a dashboard. A weekly operating review installed with leadership, plus the playbook, cadence and roadmap handed to the client's operations lead."],
];

const outcomes: Array<[string, string, string]> = [
  ["Launch lead time", "12 weeks", "6 weeks"],
  ["Order exceptions (per 1,000 orders)", "Baseline", "−35%"],
  ["Systems of record for stock", "3", "1"],
  ["Weekly plan rebuild", "Manual, ~2 days", "Automated, reviewed in 90 minutes"],
];

const ConsumerHardware = () => (
  <PageShell
    title="Case 01 · Consumer Hardware | Skandor Technologies"
    description="An anonymised consumer-hardware engagement: the problem, what we built, and the measured outcome — −35% order exceptions and launch lead time halved in nine months."
    canonicalPath="/work/consumer-hardware"
  >
    <section className="relative overflow-hidden bg-background py-16 md:py-24">
      <HeroFields compact />
      <div className="container relative z-10 mx-auto">
        <Link to="/work" className="mb-8 inline-flex min-h-11 items-center gap-2 text-sm text-ink-3 hover:text-steel-2">
          <ArrowLeft className="h-4 w-4" /> All work
        </Link>
        <div className="max-w-4xl">
          <p className="eyebrow">Case 01 · Consumer hardware · Anonymised</p>
          <h1 className="mt-4">From manual planning to a hardened operating spine</h1>
          <p className="lede mt-5 max-w-3xl">
            A mid-market operator with a product customers loved and an operation that could not keep up.
            Nine months to one connected planning-to-fulfilment backbone, a weekly resilience view and a
            launch cadence roughly twice as fast.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[["−50%", "Launch lead time"], ["−35%", "Order exceptions"], ["9 mo", "To hardened operations"]].map(([n, l]) => (
            <article key={l} className="surface-card group p-6">
              <strong className="metric-gradient block text-[clamp(2rem,3.2vw,2.7rem)] font-bold leading-none">{n}</strong>
              <p className="mt-4 text-sm text-ink-2">{l}</p>
            </article>
          ))}
        </div>

        <div className="surface-card mt-4 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-5">
          {profile.map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[.66rem] text-steel-2">{k.toUpperCase()}</p>
              <p className="mt-1 text-sm font-medium text-ink">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-tint py-16 md:py-24">
      <div className="container mx-auto max-w-5xl">
        <p className="eyebrow">The problem</p>
        <h2 className="mt-4">The product scaled. The operation underneath it did not.</h2>
        <p className="lede mt-5 max-w-3xl">
          Two new markets were planned for the year ahead. On the operation as it stood, each one would have
          added headcount and cost margin.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {problems.map(([t, b]) => (
            <article key={t} className="surface-card p-6">
              <h3>{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{b}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-5xl">
        <p className="eyebrow">The solution</p>
        <h2 className="mt-4">Diagnose, build, operationalise — one accountable team.</h2>
        <ol className="mt-8 space-y-4">
          {solution.map(([n, t, b]) => (
            <li key={t} className="surface-card p-6 md:p-7">
              <p className="font-mono text-[.66rem] text-steel-2">STAGE {n}</p>
              <h3 className="mt-3">{t}</h3>
              <p className="mt-2 leading-relaxed text-ink-2">{b}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="section-tint py-16 md:py-24">
      <div className="container mx-auto max-w-5xl">
        <p className="eyebrow">The outcome</p>
        <h2 className="mt-4">Measured against the twelve weeks before we started.</h2>
        <div className="surface-card mt-8 overflow-x-auto p-2">
          <table className="w-full min-w-[34rem] border-collapse text-left">
            <caption className="sr-only">Measures before and after the engagement</caption>
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-3 font-mono text-[.66rem] font-medium text-steel-2">MEASURE</th>
                <th scope="col" className="px-4 py-3 font-mono text-[.66rem] font-medium text-steel-2">BEFORE</th>
                <th scope="col" className="px-4 py-3 font-mono text-[.66rem] font-medium text-steel-2">AFTER</th>
              </tr>
            </thead>
            <tbody>
              {outcomes.map(([m, before, after]) => (
                <tr key={m} className="border-b border-line last:border-0">
                  <th scope="row" className="px-4 py-4 text-sm font-medium text-ink">{m}</th>
                  <td className="px-4 py-4 text-sm text-ink-3">{before}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-ink">{after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-ink-2">
          Leadership now runs the business from one weekly view, and the client&apos;s own operations lead owns the
          cadence. Skandor left at handover.
        </p>
      </div>
    </section>

    <section className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-4xl">
        <div className="surface-card p-6 md:p-7">
          <p className="font-mono text-[.66rem] text-steel-2">HOW THIS CASE IS REPORTED</p>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            The client is not named: every Skandor engagement runs under NDA, so identities stay confidential
            and only the shape of the work and the measured numbers are published. Figures compare a verified
            12-week pre-engagement baseline with a 12-week post-engagement measurement, agreed with the client
            before the engagement began. <strong className="font-semibold text-ink">References are available under NDA</strong> and can be
            arranged after a first conversation.
          </p>
        </div>
        <div className="mt-14 border-t border-line pt-10 text-center">
          <h2>A similar operating challenge?</h2>
          <Button asChild className="mt-6">
            <a href="/#contact">Talk to a senior partner <ArrowRight /></a>
          </Button>
        </div>
      </div>
    </section>
  </PageShell>
);

export default ConsumerHardware;
